import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { StoreVisitMemberWithTicketList } from './lib/types';

type CmtTicket = StoreVisitMemberWithTicketList & { ticketType: 'COMMUTATION' };
type VerificationStatus = 'confirmed' | 'pending';
type StatusEntry = { value: VerificationStatus; at: string };

export type CmtVerificationResult = {
	ticketId: number;
	result: 'MATCH' | 'HOLD';
	/** 선택일시 (= 이벤트 생성일시) */
	selectedAt: string;
};

type Props = {
	open: boolean;
	tickets: CmtTicket[];
	onComplete: (results: CmtVerificationResult[]) => void;
};

export const CmtTicketVerificationModal = ({ open, tickets, onComplete }: Props) => {
	const [status, setStatus] = useState<Record<number, StatusEntry>>({});
	// 한 번에 하나의 정기권만 펼쳐 확인하도록 한다.
	const [expandedId, setExpandedId] = useState<number | null>(null);

	// 부모의 cmtTickets 는 useMemo([data]) 라서 백그라운드 refetch 때마다 내용이 같아도
	// 배열 참조가 새로 생긴다. tickets 참조에 의존해 초기화하면 본인 확인 도중 refetch 가
	// 겹칠 때 선택값(status)이 풀린다(D-6). 그래서 참조가 아니라 ticketId 구성이 실제로
	// 바뀔 때만 초기화한다 — 같은 스캔의 refetch 는 유지, 새 스캔은 초기화된다.
	const ticketsKey = tickets.map((t) => t.ticketId).join(',');
	useEffect(() => {
		setStatus({});
		setExpandedId(tickets[0]?.ticketId ?? null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketsKey]);

	const allVerified =
		tickets.length > 0 && tickets.every((t) => status[t.ticketId] !== undefined);

	const handleSelect = (ticketId: number, value: VerificationStatus) => {
		const nextStatus: Record<number, StatusEntry> = {
			...status,
			[ticketId]: { value, at: dayjs().toISOString() },
		};
		setStatus(nextStatus);
		// 선택한 정기권은 접고 다음 미확인 정기권을 펼친다. 모두 완료되면 모두 접는다.
		const nextTicket = tickets.find((t) => nextStatus[t.ticketId] === undefined);
		setExpandedId(nextTicket ? nextTicket.ticketId : null);
	};

	const handleComplete = () => {
		const results: CmtVerificationResult[] = tickets.map((t) => ({
			ticketId: t.ticketId,
			result: status[t.ticketId].value === 'pending' ? 'HOLD' : 'MATCH',
			selectedAt: status[t.ticketId].at,
		}));
		onComplete(results);
	};

	return (
		<Dialog
			open={open}
			maxWidth='sm'
			fullWidth
			disableEscapeKeyDown>
			<DialogTitle>{'정기권 본인 확인'}</DialogTitle>
			<DialogContent>
				<Typography
					variant='body2'
					color='text.secondary'
					sx={{ mb: 2 }}>
					{'정기권 이용자의 사진을 확인하고 본인 일치 여부를 선택해 주세요.'}
				</Typography>
				<Stack spacing={1}>
					{tickets.map((ticket) => (
						<CmtTicketCard
							key={ticket.ticketId}
							ticket={ticket}
							status={status[ticket.ticketId]?.value}
							expanded={expandedId === ticket.ticketId}
							onToggle={() =>
								setExpandedId((prev) => (prev === ticket.ticketId ? null : ticket.ticketId))
							}
							onConfirm={() => handleSelect(ticket.ticketId, 'confirmed')}
							onPending={() => handleSelect(ticket.ticketId, 'pending')}
						/>
					))}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 3 }}>
				<Button
					variant='contained'
					fullWidth
					disabled={!allVerified}
					onClick={handleComplete}>
					{'확인 완료'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const genderLabel = (gender: 'MALE' | 'FEMALE' | 'UNKNOWN') => {
	if (gender === 'MALE') return '남';
	if (gender === 'FEMALE') return '여';
	return '-';
};

// FE 는 URL 이 올바른지 사전에 알 수 없으므로, 빈 URL 은 직접 걸러내고
// 로드 실패(404·네트워크 등)는 img 의 onError 로 감지해 플레이스홀더로 대체한다.
// 사진이 없어도 본인 확인 선택은 막지 않는다 (3.3 스펙: 발권 흐름에 영향 없음).
const CmtUserPhoto = ({ imageUrl, username }: { imageUrl?: string; username: string }) => {
	const [failed, setFailed] = useState(false);
	const noPhoto = !imageUrl?.trim() || failed;

	return (
		<Box
			sx={{
				width: '100%',
				aspectRatio: 1,
				overflow: 'hidden',
				borderRadius: 1,
				bgcolor: 'grey.100',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}>
			{noPhoto ? (
				<Stack
					alignItems='center'
					spacing={1}
					color='text.secondary'
					sx={{ px: 2, textAlign: 'center' }}>
					<PersonOutlineIcon sx={{ fontSize: 56 }} />
					<Typography variant='body2'>
						{!imageUrl?.trim() ? '등록된 사진이 없습니다' : '사진을 불러올 수 없습니다'}
					</Typography>
					<Typography variant='caption'>{'다른 방법으로 본인 확인 후 선택해 주세요.'}</Typography>
				</Stack>
			) : (
				<img
					src={imageUrl}
					alt={username}
					onError={() => setFailed(true)}
					style={{ width: '100%', height: '100%', objectFit: 'cover' }}
				/>
			)}
		</Box>
	);
};

const CmtTicketCard = ({
	ticket,
	status,
	expanded,
	onToggle,
	onConfirm,
	onPending,
}: {
	ticket: CmtTicket;
	status: VerificationStatus | undefined;
	expanded: boolean;
	onToggle: () => void;
	onConfirm: () => void;
	onPending: () => void;
}) => (
	<Accordion
		expanded={expanded}
		onChange={onToggle}>
		<AccordionSummary expandIcon={<ExpandMoreIcon />}>
			<Stack
				direction='row'
				alignItems='center'
				justifyContent='space-between'
				width='100%'
				pr={1}>
				<Typography>{ticket.cmtUserInfo.username}</Typography>
				{status === 'confirmed' && (
					<Chip
						label='본인 일치'
						color='primary'
						size='small'
					/>
				)}
				{status === 'pending' && (
					<Chip
						label='보류'
						size='small'
					/>
				)}
			</Stack>
		</AccordionSummary>
		<AccordionDetails>
			<Stack spacing={2}>
				<CmtUserPhoto
					imageUrl={ticket.cmtUserInfo.imageUrl}
					username={ticket.cmtUserInfo.username}
				/>
				<Stack spacing={0.5}>
					<Typography variant='body2'>{`이름: ${ticket.cmtUserInfo.username}`}</Typography>
					<Typography variant='body2'>
						{`생년월일: ${dayjs(ticket.cmtUserInfo.birthday).format('YYYY년 MM월 DD일')}`}
					</Typography>
					<Typography variant='body2'>{`성별: ${genderLabel(ticket.cmtUserInfo.gender)}`}</Typography>
				</Stack>
				<Stack
					direction='row'
					spacing={1}>
					<Button
						variant={status === 'confirmed' ? 'contained' : 'outlined'}
						color='primary'
						fullWidth
						onClick={onConfirm}>
						{'본인 일치'}
					</Button>
					<Button
						variant={status === 'pending' ? 'contained' : 'outlined'}
						color='inherit'
						fullWidth
						onClick={onPending}>
						{'보류'}
					</Button>
				</Stack>
			</Stack>
		</AccordionDetails>
	</Accordion>
);
